import CancelIcon from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputLabel, MenuItem, Select, Skeleton } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from '../components/ConfirmationDialog';
import LazyLoader from '../components/LazyLoader';
import { useApi } from '../hooks/useApi';
import DataCard from "./DataCard";

export default function DataTable({ userInfo, onSelect, selectable = false,
  usageFlags = { pdf: true, svg: true, image: true, model: true },
  trash = false, all = false, multiSelectEnabled = false }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [endReached, setEndReached] = useState(false);
  // sorting
  const [sortType, setSortType] = useState('UPLOAD_DATE');
  const [sortDir, setSortDir] = useState('DESC');
  const [data, setData] = useState([0, 1, 2, 3, 4, 5, 6, 7, 8]);
  const [updateCounter, setUpdateCounter] = useState(0);
  const [pdfFlag, setPdfFlag] = useState(usageFlags.pdf ? true : false);
  const [svgFlag, setSvgFlag] = useState(usageFlags.svg ? true : false);
  const [imageFlag, setImageFlag] = useState(usageFlags.image ? true : false);
  const [modelFlag, setModelFlag] = useState(usageFlags.model ? true : false);
  const [isLoading, setIsLoading] = useState(false);

  // state for force-deletion-dialog
  const [isOpenForceDialog, setIsOpenForceDialog] = useState({ state: false, id: null, versioning: null });

  // state for saving multi-selection
  const [multiSelect, setMultiSelect] = useState({});
  // only used for resetting the checked-state of DataCard-components
  const [clearCounter, setClearCounter] = useState(0);

  const fileRef = useRef();

  const navigate = useNavigate();

  const intl = useIntl();
  const api = useApi();

  /**
   * Whenever the page is changed, fetch new data (except for first page).
   */
  useEffect(() => {
    // do not fetch first page here
    if (page === 1) return;
    fetchData(page);
  }, [page]);

  /**
   * Clear the data and start new fetch.
   */
  useEffect(() => {
    setData([0, 1, 2, 3, 4, 5, 6, 7, 8]);
    setPage(1);
    setEndReached(false);
    fetchData(1);
  }, [updateCounter, pageSize, pdfFlag, svgFlag, imageFlag, modelFlag, sortType, sortDir, all, trash])

  const fetchData = (page) => {
    setIsLoading(true);
    api
      .get(`/data?all=${all}&trash=${trash}&pdf=${pdfFlag}&svg=${svgFlag}&image=${imageFlag}&model=${modelFlag}&page=${page - 1}&size=${pageSize}&sortType=${sortType}&sortDir=${sortDir}`)
      .then(response => {
        if (page === 1) {
          setData(response.data.content);
        } else {
          setData(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        navigate('/login');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Should be called to delete data (either placing in trash or completely removing).
   * @param {*} id
   * @param {*} force
   * @returns 
   */
  const onDelete = (id, force = false, forceVersioning = false) => () => {
    api
      .delete(`/data${trash ? '/trash' : ''}/${id}?force=${force}&forceVersioning=${forceVersioning}`)
      .then(() => {
        setUpdateCounter(counter => counter + 1);
        if (force || forceVersioning) {
          handleCloseDialog();
        }
      })
      .catch(error => {
        if (((!force) || (force && !forceVersioning)) && error.response && error.response.status === 409) {
          // process must be repeated with the force-flag, display dialog
          handleOpenDialog(id, false, error.response.data.versionedAssociations);
        } else {
          console.log(error);
          if (force || forceVersioning) {
            handleCloseDialog();
          }
        }
      });
  };

  /**
   * Deletes a list of data (either placing in trash or completely removing).
   * @param {*} force 
   * @returns 
   */
  const onDeleteFromList = (force = false, forceVersioning = false) => () => {
    api
      .delete(`/data${trash ? '/trash' : ''}/list?ids=${Object.keys(multiSelect)}&force=${force}&forceVersioning=${forceVersioning}`)
      .then(() => {
        setUpdateCounter(counter => counter + 1);
        if (force || forceVersioning) {
          handleCloseDialog();
        }
        clearMultipleSelectData();
      })
      .catch(error => {
        if (((!force) || (force && !forceVersioning)) && error.response && error.response.status === 409) {
          // process must be repeated with the force-flag, display dialog
          handleOpenDialog(null, true, error.response.data.versionedAssociations);
        } else {
          console.log(error);
          if (force || forceVersioning) {
            handleCloseDialog();
          }
        }
      });
  };

  /**
   * Should be called to recover data from the trash.
   * @param {*} data 
   * @returns 
   */
  const onRecover = (data) => () => {
    api
      .put(`/data/trash/${data.id}`)
      .then(() => {
        setUpdateCounter(counter => counter + 1);
      })
      .catch(error => {
        console.log(error);
      })
  };

  /**
   * Recovers a list of data from the trash.
   * @param {*} force 
   */
  const onListRecover = (force = false) => {
    api
      .put(`/data/trash/list?ids=${Object.keys(multiSelect)}`)
      .then(response => {
        setUpdateCounter(counter => counter + 1);
        clearMultipleSelectData();
      })
      .catch(error => {
        console.log(error);
      }
      );
  };

  /**
   * Should be called to download data.
   * @param {*} data 
   * @returns 
   */
  const onDownload = (data) => () => {
    api
      .get(`/data/${data.id}/file`, { responseType: 'blob' })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.download = data.fileName;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      })
      .catch(error => {
        console.log(error);
      })
  }

  /**
   * Handle selecting a file in the file-drawer that is shown to the user.
   * @param {*} files 
   */
  const handleFileSelect = (files) => {
    const reqs = [];
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('file', files[i]);
      let url = '/data';
      if (files[i].lastModified) {
        url += '?lastModified=' + files[i].lastModified;
      }
      reqs.push(api.post(url, formData));
    }
    const promises = Promise.all(reqs);

    promises
      .then(() => {
        setUpdateCounter(counter => counter + 1);
      })
      .catch(error => {
        console.log(error);
      });
  }

  /**
   * Returns the abstract, high-level type of the specified contentType.
   * @param {*} contentType
   */
  const getAbstractType = (contentType) => {
    if (contentType === 'application/pdf') {
      return 'pdf';
    } else if (contentType.startsWith('model')) {
      return 'model';
    } else if (contentType === 'application/octet-stream') {
      return 'other';
    } else if (contentType === 'application/zip') {
      return 'model';
    } else {
      return 'image';
    }
  };

  /**
   * Should be called when the sorting-type is changed.
   * @param {*} event 
   */
  const handleChangeSortType = (event) => {
    setSortType(event.target.value);
  }

  /**
   * Should be called when the sorting-direction is changed.
   * @param {*} event 
   */
  const handleChangeSortDir = (event) => {
    setSortDir(event.target.value);
  }

  /**
   * Adds the specified id to the selected data.
   * @param {*} id 
   */
  const addMultipleSelectData = (id) => {
    setMultiSelect(old => ({ ...old, [id]: true }));
  };

  /**
   * Removes the specified id from the selected data.
   * @param {*} id 
   */
  const removeMultipleSelectData = (id) => {
    setMultiSelect(old => {
      let newOb = { ...old };
      delete newOb[id];
      return newOb;
    });
  };

  /**
   * Clears the selected data.
   */
  const clearMultipleSelectData = () => {
    setMultiSelect({});
    setClearCounter(old => old + 1);
  };

  /**
   * Should be called when the checkbox is de-/selected.
   * @param {*} id 
   * @param {*} value 
   */
  const handleChangeCheckbox = (id, value) => {
    if (value) {
      addMultipleSelectData(id);
    } else {
      removeMultipleSelectData(id);
    }
  };

  /**
   * Should be called to open the force-deletion-dialog.
   * @param {*} id 
   * @param {*} fromTrash 
   */
  const handleOpenDialog = (id, multiple, versioning) => {
    setIsOpenForceDialog({ state: true, id, multiple, versioning });
  }

  /**
   * Should be called to close the force-deletion-dialog
   */
  const handleCloseDialog = () => {
    setIsOpenForceDialog({ state: false, id: null, multiple: false, versioning: null });
  }

  /**
   * The force-deletion-dialog.
   */
  const dialog = useMemo(() => {
    let onDeleteFunction = () => { };
    if (isOpenForceDialog.multiple) {
      onDeleteFunction = onDeleteFromList(true, isOpenForceDialog.versioning);
    } else {
      onDeleteFunction = onDelete(isOpenForceDialog.id, true, isOpenForceDialog.versioning);
    }
    let title = "Data.deletionQuestion";
    if (isOpenForceDialog.multiple) {
      if (isOpenForceDialog.versioning) {
        title = "Data.multipleDeletionQuestionVersioned";
      } else {
        title = "Data.multipleDeletionQuestion";
      }
    } else {
      if (isOpenForceDialog.versioning) {
        title = "Data.deletionQuestionVersioned";
      } else {
        title = "Data.deletionQuestion";
      }
    }
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: title })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpenForceDialog.state}
        handleAccept={onDeleteFunction}
        handleClose={handleCloseDialog}
      />
    );
    if (isOpenForceDialog.state) {
      return d;
    }
  }, [isOpenForceDialog, intl]);

  const dataElements = useMemo(() => {
    return <Grid container spacing={1} sx={{}}>
      {
        data.map(data => {
          return (
            <Grid item xs={12} sm={6} md={4} key={data.id} >
              {
                (isLoading || !data.id) ? (
                  <Skeleton variant="rectangular" width="100%" height="300px" />
                ) : (
                  data && <DataCard
                    userInfo={userInfo}
                    data={data}
                    abstractType={getAbstractType(data.fileFormat)}
                    selectable={selectable}
                    onSelect={trash ? onRecover : onSelect}
                    onDelete={onDelete}
                    onDownload={onDownload}
                    trash={trash}
                    initialChecked={multiSelect[data.id]}
                    handleChangeCheckbox={handleChangeCheckbox}
                    multiSelectEnabled={multiSelectEnabled}
                    clearCounter={clearCounter}
                  />
                )
              }
            </Grid>
          )
        })
      }
    </Grid>
  }, [data]);

  return (
    <Box>
      <Box sx={{ marginBottom: 1 }}>
        <FormGroup sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
          {usageFlags.pdf && <FormControlLabel control={<Checkbox checked={pdfFlag} onChange={(event) => { setPdfFlag(event.target.checked); }} />} label="PDF" />}
          {usageFlags.svg && <FormControlLabel control={<Checkbox checked={svgFlag} onChange={(event) => { setSvgFlag(event.target.checked); }} />} label="SVG" />}
          {usageFlags.image && <FormControlLabel control={<Checkbox checked={imageFlag} onChange={(event) => { setImageFlag(event.target.checked); }} />} label="JPG / PNG" />}
          {usageFlags.model && <FormControlLabel control={<Checkbox checked={modelFlag} onChange={(event) => { setModelFlag(event.target.checked); }} />} label="GLTF / GLB / FBX / OBJ / ZIP" />}
        </FormGroup>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 200 }}>
          <InputLabel id="sorting-type-select-standard-label"><FormattedMessage id="Overview.sortBy" /></InputLabel>
          <Select
            labelId="sorting-type-select-standard-label"
            id="sorting-type-select-standard"
            value={sortType}
            onChange={handleChangeSortType}
            label={intl.formatMessage({ id: "Overview.sortBy" })}
          >
            <MenuItem value="UPLOAD_DATE">
              <em><FormattedMessage id="Data.uploadTime" /></em>
            </MenuItem>
            <MenuItem value="CREATION_DATE">
              <em><FormattedMessage id="Data.creationTime" /></em>
            </MenuItem>
            <MenuItem value="NAME">
              <em><FormattedMessage id="Data.name" /></em>
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 200 }}>
          <InputLabel id="sorting-direction-select-standard-label"><FormattedMessage id="Overview.sortDirection" /></InputLabel>
          <Select
            labelId="sorting-direction-select-standard-label"
            id="sorting-direction-select-standard"
            value={sortDir}
            onChange={handleChangeSortDir}
            label={intl.formatMessage({ id: "Overview.sortDirection" })}
          >
            <MenuItem value="DESC">
              <em><FormattedMessage id="Overview.desc" /></em>
            </MenuItem>
            <MenuItem value="ASC">
              <em><FormattedMessage id="Overview.asc" /></em>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={1} justifyContent="center" sx={{ border: '1px dashed black', borderRadius: 3, width: "100%", paddingBottom: 1, paddingRight: 1, marginBottom: 2 }} >
        <Grid item xs={12} >
          <input multiple accept=".jpg, .jpeg, .png, .webp, .svg, .pdf, .fbx, .gltf, .glb, .obj, .zip" ref={fileRef} hidden type="file" onChange={event => handleFileSelect(event?.target?.files)} onClick={event => event.target.value = ''} />
          {!trash && <Box sx={{
            backgroundColor: "#bfc9c2", width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center",
            borderStyle: "dashed", boxSizing: "border-box", flexDirection: "column", "&:hover": { cursor: "pointer" }, padding: '16px'
          }} onClick={() => {
            fileRef.current?.click()
          }}>
            <CloudUploadIcon />
            <p><FormattedMessage id="Data.uploadData" /></p>
          </Box>}
        </Grid>
        <Grid item xs={12} sx={{ height: "100%" }} >
          <Box sx={{ width: "100%", height: "100%", margin: 0, padding: 0 }}>
            <LazyLoader
              onLoad={() => setPage(prev => prev + 1)}
              loading={isLoading}
              endReached={endReached}
            >
              {dataElements}
            </LazyLoader>
          </Box>
        </Grid >
      </Grid >
      {Object.keys(multiSelect).length > 0 &&
        <Box sx={{ marginBottom: 2, paddingBottom: 2, paddingTop: 1, marginRight: 2 }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            gap: 4,
            flexWrap: 'wrap',
            position: 'sticky',
            bottom: '0px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)'
          }} >
          <Button variant="contained" onClick={onDeleteFromList(false)} ><FormattedMessage id="Data.deleteSelected" /></Button>
          {trash && <Button variant="contained" onClick={onListRecover} ><FormattedMessage id="Data.recoverSelected" /></Button>}
          <IconButton color="primary" onClick={clearMultipleSelectData} ><CancelIcon fontSize="medium" /></IconButton>
        </Box>}
      {dialog}
    </Box >
  );
}